import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { UserMenu } from './UserMenu';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { ReminderBell } from '@/components/reminders/ReminderBell';
import { ReminderPopup } from '@/components/reminders/ReminderPopup';
import { AiAssistant } from '@/components/ai/AiAssistant';
import { NpsWidget } from '@/components/feedback/NpsWidget';
import { FeedbackButton } from '@/components/feedback/FeedbackButton';
import { WelcomeModal } from '@/components/onboarding/WelcomeModal';
import { RealtimeStatus } from '@/components/RealtimeStatus';
import { useWhatsAppRealtime } from '@/features/whatsapp/hooks/useWhatsAppRealtime';

interface DashboardLayoutProps {
  children: ReactNode;
  title?: string;
}

// Tailwind's `md` breakpoint — matches when we switch from mobile drawer
// sidebar to the inline desktop sidebar.
const DESKTOP_BP = 768;

export function DashboardLayout({ children, title }: DashboardLayoutProps) {
  const location = useLocation();
  // Desktop: collapsed = narrow icon-only sidebar.
  const [collapsed, setCollapsed] = useState(false);
  // Mobile: drawer open/closed. Starts closed so content gets full width.
  const [mobileOpen, setMobileOpen] = useState(false);
  // Mount realtime ONCE per logged-in session so WhatsApp notifications
  // + sound alerts work on every page (not just the inbox). The hook
  // is a no-op when not authenticated.
  useWhatsAppRealtime();

  // Close the mobile drawer whenever the user navigates (route change).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open so iOS Safari
  // doesn't bounce-scroll the page behind the sheet.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const onHamburger = () => {
    if (window.innerWidth < DESKTOP_BP) setMobileOpen((p) => !p);
    else setCollapsed((p) => !p);
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: '#F8F9FA' }}>
      {/* Desktop sidebar — inline on md+, hidden on mobile. */}
      {/* data-app-sidebar: side drawers (e.g. Leads list → lead detail) open
          up to this edge so the sidebar stays usable. */}
      <div className="hidden md:block" data-app-sidebar>
        <Sidebar collapsed={collapsed} />
      </div>

      {/* Mobile sidebar — fixed drawer, slides in from left. */}
      <div
        className={`md:hidden fixed inset-0 z-40 ${mobileOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!mobileOpen}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity ${
            mobileOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileOpen(false)}
        />
        {/* Drawer — the Sidebar component already renders its own brand
            header, so we just overlay a small floating close button
            instead of duplicating the BrixLead wordmark. */}
        <div
          className={`absolute top-0 left-0 h-full w-64 bg-white shadow-xl transition-transform ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute top-4 right-3 z-10 text-[#64748B] hover:text-[#6366F1] p-1"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
          {/* Always-expanded in mobile drawer — no room for a collapsed icon strip on a small screen. */}
          <Sidebar collapsed={false} />
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* Top Header */}
        <header className="flex h-14 sm:h-16 items-center justify-between bg-white border-b border-gray-100 px-3 sm:px-6 flex-shrink-0 gap-2">
          {/* Left: hamburger + title */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button
              onClick={onHamburger}
              className="text-[#64748B] hover:text-[#6366F1] transition-colors p-1 flex-shrink-0"
              aria-label="Toggle navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            {title && (
              <h1 className="text-base sm:text-xl font-bold text-[#6366F1] truncate">{title}</h1>
            )}
          </div>

          {/* Right: Realtime status | Notifications | Reminders | User */}
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <RealtimeStatus />
            <NotificationBell />
            <ReminderBell />
            <UserMenu />
          </div>
        </header>

        {/* Page content — tighter padding on mobile. */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-3 sm:p-6">{children}</div>
        </main>
      </div>

      {/* Auto-popup: appears center-screen when a reminder is due */}
      <ReminderPopup />
      <AiAssistant />
      <NpsWidget />
      <FeedbackButton />
      <WelcomeModal />
    </div>
  );
}
